import { initializeApp } from 'firebase/app'
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging'
import notificationStore from '@vue3/store/notification-store'
import authStore from '@vue3/store/auth-store'
import CommonStore from '@vue3/store/common-store.js'
import { isAndroidAppCheck, postEventMessageToAndroid, isIOSAppCheck, postEventMessageToIOS } from '@vue3/utils/common'
import router from '@vue3/router/index.js'
import tracker from '@vue3/constants/tracker'

const { ALLOW_NOTIIFICATION, DENY_NOTIFICATION } = tracker.label

const { NOTIFICATION } = tracker.component
const trackerOriginScreens = tracker.origin_screen || {}

let firebaseApp
let messaging
let serviceWorkerRegistration

function getTrackerPageType () {
  const currentRouteName = router?.currentRoute?.value?.name
  if (!currentRouteName) {
    return ''
  }
  return trackerOriginScreens[currentRouteName] || ''
}

function getTrackerBaseParams (authInstance) {
  const auth = authInstance || authStore()
  const common = CommonStore()
  const isLoggedIn = auth?.isLoggedIn

  const userId = isLoggedIn
    ? (common.registrationId || 'LOGGED_OUT_USER')
    : 'LOGGED_OUT_USER'

  let dealerId = 'LOGGED_OUT_USER'
  if (isLoggedIn) {
    dealerId = auth.getDealerId || 'LOGGED_IN_WITHOUT_DEALER_ID'
  }

  return {
    origin_screen: getTrackerPageType(),
    user_id: userId,
    dealer_id: dealerId
  }
}

function pushNotificationPermissionTracker (trackValue, authInstance) {
  if (typeof window === 'undefined') {
    return
  }

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ params: null, ecommerce: null })

  const trackerData = {
    event: 'button_click',
    params: {
      ...getTrackerBaseParams(authInstance),
      component: NOTIFICATION,
      ...trackValue
    }
  }

  window.dataLayer.push(trackerData)
}

function getPlatform () {
  if (isAndroidAppCheck) return 'ANDROID'
  if (isIOSAppCheck) return 'IOS'
  return 'WEB'
}

function isCompanyRegistrationApproved () {
  const auth = authStore()
  return auth?.userDetails?.companyDetails?.companyRegistrationStatus === 'APPROVED'
}

export function checkForFCMSupport () {
  return isSupported().catch(() => false)
}

function getFirebaseConfig () {
  const decodedConfig = decodeURIComponent(window.VUE_APP_RESELLER_FCM_CONFIGS)
  return JSON.parse(decodedConfig)
}

export function initializeFirebaseApp () {
  if (firebaseApp) return firebaseApp
  const firebaseConfig = getFirebaseConfig()
  firebaseApp = initializeApp(firebaseConfig)
  return firebaseApp
}

export async function registerFcmServiceWorker () {
  const supported = await isSupported().catch(() => false)
  if (!supported) {
    return null
  }
  if (!('serviceWorker' in navigator)) {
    return null
  }
  if (!firebaseApp) initializeFirebaseApp()

  serviceWorkerRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
    type: 'module',
    updateViaCache: 'none'
  })

  serviceWorkerRegistration.update().catch(() => { })

  await navigator.serviceWorker.ready

  const firebaseConfig = getFirebaseConfig()

  const sendConfigToSW = () => {
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'FIREBASE_CONFIG',
        config: firebaseConfig
      })
    }
  }

  sendConfigToSW()

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    sendConfigToSW()
  })

  try {
    messaging = getMessaging(firebaseApp)
  } catch (error) {
    return null
  }

  return serviceWorkerRegistration
}
function createNativeAppTokenCallback () {
  return (token, shouldSendTracker) => {
    if (token && token !== 'null') {
      // Permission granted - save token to server
      saveTokenToServer(token)
    } else {
      // Permission denied (token is null or 'null' string from Android)
      const cachedToken = getCachedToken()

      if (cachedToken) {
        // Delete the old token from server
        deleteTokenFromServer({
          success: () => {
            // Clear local storage
            clearTokenCache()
            // Clear native permission flag since permission was revoked
            clearNativePermissionGranted()
            // Update auth store
            authStore().setIsNotificationPermissionGranted(false)
          }
        })
      } else {
        // No cached token, just clear flag and update auth store
        clearTokenCache()
        clearNativePermissionGranted()
        authStore().setIsNotificationPermissionGranted(false)
      }
    }

    const auth = authStore()
    if (shouldSendTracker) {
      const isTokenPresent = token && token !== 'null'
      const trackLabel = isTokenPresent ? ALLOW_NOTIIFICATION : DENY_NOTIFICATION
      const trackValue = {
        cp1: trackLabel,
        cp2: auth.userDetails?.email,
        trms_id: 'DEV-001'
      }
      pushNotificationPermissionTracker(trackValue, auth)
    }
  }
}

export async function handlePermissionsForApps () {
  const isAndroidApp = isAndroidAppCheck
  const isIOSApp = isIOSAppCheck

  if (isAndroidApp || isIOSApp) {
    // Check if permission was granted through modal
    const permissionGranted = isNativePermissionGranted()

    if (!permissionGranted) {
      // Modal not shown yet or permission not granted, don't set up callback or request token
      return
    }

    // Set up callback to handle token from native apps
    window.notificationCallBack = createNativeAppTokenCallback()
  }

  if (isAndroidApp) {
    postEventMessageToAndroid({
      notificationTrigger: true
    })
  }

  if (isIOSApp) {
    postEventMessageToIOS('iosListener', 'NOTIFICATION_TRIGGER')
  }
}

// Function to handle modal acceptance for native apps
export async function handleNativeAppModalAcceptance () {
  const isAndroidApp = isAndroidAppCheck
  const isIOSApp = isIOSAppCheck

  if (!isAndroidApp && !isIOSApp) {
    return
  }

  // Mark that permission was granted through modal
  markNativePermissionGranted()

  // Set up the callback to receive token from native app
  window.notificationCallBack = createNativeAppTokenCallback()

  // Request token from native app
  if (isAndroidApp) {
    postEventMessageToAndroid({
      notificationTrigger: true
    })
  }

  if (isIOSApp) {
    postEventMessageToIOS('iosListener', 'NOTIFICATION_TRIGGER')
  }
}

// Function to request fresh token from native apps
export function requestTokenFromNativeApp () {
  if (isAndroidAppCheck) {
    postEventMessageToAndroid({
      notificationTrigger: true
    })
  } else if (isIOSAppCheck) {
    postEventMessageToIOS('iosListener', 'NOTIFICATION_TRIGGER')
  }
}

export async function requestNotificationPermissionAndGetToken () {
  handlePermissionsForApps()
  const supported = await isSupported().catch(() => false)
  if (!supported) throw new Error('Notifications are not supported on this browser/device')

  if (Notification.permission !== 'granted') {
    const permission = await Notification.requestPermission()

    if (permission !== 'granted') {
      throw new Error('User did not grant notifications permission')
    }
  }

  if (!serviceWorkerRegistration) {
    await registerFcmServiceWorker()
  }
  if (!messaging) {
    if (!firebaseApp) initializeFirebaseApp()
    try {
      messaging = getMessaging(firebaseApp)
    } catch (error) {
      throw new Error('Failed to initialize Firebase Messaging: ' + error.message)
    }
  }

  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY
  const currentToken = await getToken(messaging, {
    vapidKey,
    serviceWorkerRegistration
  }).catch((error) => {
    throw error
  })

  if (currentToken) {
    return currentToken
  }
  throw new Error('No registration token available. Request permission to generate one.')
}

export async function getTokenIfPermitted () {
  const supported = await isSupported().catch(() => false)
  if (!supported) return ''
  if (Notification.permission !== 'granted') return ''
  if (!serviceWorkerRegistration) {
    await registerFcmServiceWorker()
  }
  if (!messaging) {
    if (!firebaseApp) initializeFirebaseApp()
    try {
      messaging = getMessaging(firebaseApp)
    } catch (error) {
      return ''
    }
  }
  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY
  try {
    const token = await getToken(messaging, { vapidKey, serviceWorkerRegistration })
    return token || ''
  } catch (err) {
    return ''
  }
}

export function listenToForegroundMessages (callback) {
  try {
    if (!messaging) {
      if (!firebaseApp) initializeFirebaseApp()
      messaging = getMessaging(firebaseApp)
    }
    onMessage(messaging, (payload) => {
      try {
        callback && callback(payload)
      } catch (err) {
        console.warn('[FCM] Error in foreground message callback:', err)
      }
    })
  } catch (error) {
    console.warn('[FCM] Failed to setup foreground message listener:', error)
  }
}

export function saveTokenToServer (token) {
  const store = notificationStore()
  const auth = authStore()
  const registrationId = auth.getRegistrationId

  if (!registrationId) {
    return
  }

  if (!isCompanyRegistrationApproved()) {
    return
  }

  const cachedToken = getCachedToken()
  const tenantList = auth.tenantList.map(t => t?.tenantId).filter(Boolean)

  const payload = {
    tenantList,
    registrationToken: token,
    platform: getPlatform()
  }

  // If there's a cached token that's different from the new one,
  // delete the old token from server first to prevent duplicates
  if (cachedToken && cachedToken !== token) {
    store.deleteToken({
      data: { tenantList, registrationToken: cachedToken, platform: 'WEB' },
      success: () => {
        // After deleting old token, save the new one
        saveNewToken()
      },
      fail: () => {
        // Even if deletion fails, save the new token
        saveNewToken()
      }
    })
  } else {
    // No cached token or same token, save directly
    saveNewToken()
  }

  function saveNewToken () {
    store.saveToken({
      data: payload,
      success: () => {
        cacheTokenLocally(token)
        setTokenTimestamp()
        auth.setIsNotificationPermissionGranted(true)
      }
    })
  }
}

export function deleteTokenFromServer ({ success } = {}) {
  const store = notificationStore()
  const auth = authStore()
  const registrationId = auth.getRegistrationId
  const cachedToken = getCachedToken()
  const tenantList = (auth.tenantList || []).map(tenant => tenant?.tenantId).filter(Boolean)

  if (!registrationId || !cachedToken) {
    success && success()
    return
  }

  if (!isCompanyRegistrationApproved()) {
    success && success()
    return
  }

  store.deleteToken({
    data: { tenantList, registrationToken: cachedToken, platform: getPlatform() },
    success: (response) => {
      clearTokenCache()
      success && success(response)
    },
    fail: () => {
      success && success()
    }
  })
}

export function updateTokenTime () {
  const store = notificationStore()
  const auth = authStore()
  const registrationId = auth.getRegistrationId
  const cachedToken = getCachedToken()

  if (!registrationId || !cachedToken) {
    return
  }

  if (!isCompanyRegistrationApproved()) {
    return
  }

  store.updateTime({
    data: { tenantList: (auth.tenantList || []).map(tenant => tenant?.tenantId).filter(Boolean), registrationToken: cachedToken, platform: getPlatform() },
    success: () => {
      setTokenTimestamp()
    },
    fail: async (errorCode) => {
      if (errorCode === 'RESEND_TOKEN') {
        if (isAndroidAppCheck || isIOSAppCheck) {
          // For native apps, request fresh token
          requestTokenFromNativeApp()
        } else {
          // For web, get token from Firebase
          const token = await getTokenIfPermitted()
          if (token) {
            saveTokenToServer(token)
          }
        }
      }
    }
  })
}

export function ensureAndWatchFcmToken () {
  // For native apps, just set up token handling and check for existing tokens
  if (isAndroidAppCheck || isIOSAppCheck) {
    const permissionGranted = isNativePermissionGranted()

    // If permission is already granted, handle existing tokens
    if (permissionGranted) {
      handlePermissionsForApps()

      // Check if we have a cached token and if it needs refreshing
      const cachedToken = getCachedToken()
      if (cachedToken) {
        const shouldRefresh = shouldRefreshToken()
        if (shouldRefresh) {
          // For native apps, we can't directly refresh the token
          // The app will send a new token when it's ready
          authStore().setIsNotificationPermissionGranted(true)
          updateTokenTime()
        } else {
          authStore().setIsNotificationPermissionGranted(true)
        }
      }
    }

    return
  }

  registerFcmServiceWorker().then((registration) => {
    if (!registration) {
      return
    }

    getTokenIfPermitted().then(token => {
      if (token) {
        const cached = getCachedToken()
        const shouldRefresh = shouldRefreshToken()

        if (token !== cached) {
          saveTokenToServer(token)
        } else if (shouldRefresh) {
          authStore().setIsNotificationPermissionGranted(true)
          updateTokenTime()
        } else {
          authStore().setIsNotificationPermissionGranted(true)
        }
      }
    }).catch(() => { })
  }).catch((error) => {
    console.log('[FCM] Failed to register service worker, skipping token management:', error)
  })
}

export async function initializeFirebaseNotifications () {
  try {
    const supported = await checkForFCMSupport()

    if (!supported) {
      return
    }

    initializeFirebaseApp()

    const registration = await registerFcmServiceWorker()

    if (!registration) {
      return
    }

    listenToForegroundMessages(async (payload) => {
      const {
        notification: {
          title,
          body,
          icon
        } = {},
        fcmOptions: {
          link: clickAction
        } = {}
      } = payload || {}

      if ((title || body) && Notification.permission === 'granted') {
        try {
          const registration = await navigator.serviceWorker.ready
          const notificationData = {
            click_action: clickAction,
            FCM_MSG: payload
          }

          await registration.showNotification(title, {
            body,
            icon,
            tag: 'apple-reseller-notification',
            requireInteraction: false,
            vibrate: [200, 100, 200],
            data: notificationData
          })
        } catch (error) {
          console.log('[FCM] Failed to show notification:', error)
        }
      }
    })

  } catch (error) {
    console.warn('[FCM] Failed to initialize Firebase notifications:', error)
  }
}

function getCachedToken () {
  return localStorage.getItem('fcm_token')
}

function cacheTokenLocally (token) {
  localStorage.setItem('fcm_token', token)
}

function getTokenTimestamp () {
  return localStorage.getItem('fcm_token_timestamp')
}

function setTokenTimestamp () {
  localStorage.setItem('fcm_token_timestamp', Date.now().toString())
}

function shouldRefreshToken () {
  const timestamp = getTokenTimestamp()
  if (!timestamp) return true

  const lastUpdate = parseInt(timestamp, 10)
  const now = Date.now()
  const oneDayInMs = 24 * 60 * 60 * 1000

  return (now - lastUpdate) >= oneDayInMs
}

function clearTokenCache () {
  localStorage.removeItem('fcm_token')
  localStorage.removeItem('fcm_token_timestamp')
}

export function shouldShowNotificationModal () {
  const isNativeApp = isAndroidAppCheck || isIOSAppCheck

  // For native apps, check if modal was already shown and permission was granted
  if (isNativeApp) {
    const nativePermissionGranted = localStorage.getItem('native_notification_permission_granted')
    if (nativePermissionGranted === 'true') {
      return false
    }
  } else {
    // For web, check browser notification permission
    if (Notification.permission === 'granted') {
      return false
    }

    if (Notification.permission === 'denied') {
      return false
    }
  }

  return true
}


export function markNativePermissionGranted () {
  localStorage.setItem('native_notification_permission_granted', 'true')
}

export function clearNativePermissionGranted () {
  localStorage.removeItem('native_notification_permission_granted')
}

export function isNativePermissionGranted () {
  return localStorage.getItem('native_notification_permission_granted') === 'true'
}

// Function to handle modal rejection for native apps
export function handleNativeAppModalRejection () {
  const isNativeApp = isAndroidAppCheck || isIOSAppCheck

  if (isNativeApp) {
    // Ensure native permission flag is cleared
    clearNativePermissionGranted()
    // Update auth store
    authStore().setIsNotificationPermissionGranted(false)
  }
}

export function resetNotificationPermissions () {
  localStorage.removeItem('fcm_token')
  localStorage.removeItem('fcm_token_timestamp')
  localStorage.removeItem('native_notification_permission_granted')
}

if (typeof window !== 'undefined') {
  window.resetNotificationPermissions = resetNotificationPermissions
}
